// The page-level "Ask about this model" chat rail. It sits beside the model tabs
// (ModelPage) so it stays put on Overview / Insights / Model / Review /
// Deployment — one chat surface for the whole model, rather than one embedded in
// the Overview summary card.
//
// It talks to the same `inbound-model-builder` agent the summary card's ask box
// used to (rgipChatApi), seeded with the cached model summary as context. A
// `summary_refresh` activity regenerates the cached summary server-side and pings
// the Overview card (summaryRefreshBus); a tree write pings the tree pane
// (treeRefreshBus).
//
// An assistant turn is an ordered list of parts (chatParts.ts), not one markdown
// string: prose, jump pills and deliverable blocks render in the order the agent
// produced them. Before that, pills were collected into one array and drawn after
// the whole answer, and blocks were dropped entirely.
//
// While a turn runs, the panel shows what the agent is doing — the model's own
// thinking (`reasoning` frames, live then collapsed behind a toggle), the
// tool-call trail with per-call durations, and a turn timer — the same chrome as
// admin's ModelEditorChat, so the two chats read alike.
//
// Two things about a running turn are this panel's own, because turns here are
// long (the agent iterates on a tree) rather than one question and one answer:
// Stop takes the composer's send slot and cancels the run server-side, and the
// autoscroll only follows while the reader is already at the bottom.
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../ui/icon';
import { useSidePanelStore } from '../layout/side-panel/side-panel-store';
import { useShellPanelHost } from '../layout/side-panel/panel-host-context';
import {
  ChatActivityEvent,
  ChatMessage,
  DeliverableRef,
  ModelSummary,
  cancelRun,
  fetchAiInsightsEnabled,
  fetchDeliverableContent,
  fetchMemoryEnabled,
  fetchModelSummary,
  getTranscript,
  jumpFromActivity,
  listDeliverables,
  listModelThreads,
  handoffThread,
  isModelRefreshActivity,
  isModelsRefreshActivity,
  isRulesRefreshActivity,
  RULES_AGENT,
  TREES_AGENT,
  ThreadRow,
  resetThread,
  shouldStickToBottom,
  startTurn,
  streamRun,
  SUGGESTED_STEP,
  SUMMARY_REFRESH_KEY,
  treeRefreshFromActivity,
  isSignalsRefreshActivity,
} from './rgipChatApi';
import { Markdown } from './ChatMarkdown';
import { ChatTracker, trackTurn } from './chatAnalytics';
import { isAbort } from '../models/askTurns';
import {
  ChatPart,
  appendBlockDelta,
  appendPill,
  appendCard,
  appendScoreCard,
  appendRulesCard,
  appendThresholdsCard,
  appendSignalCard,
  appendText,
  bindDeliverable,
  replaceBlockBody,
  blockFormat,
  closeBlock,
  hasContent,
  offersAnotherModel,
  openBlock,
} from './chatParts';

import {
  claimHandoff,
  forgetOpenChat,
  markHandoff,
  recallOpenChat,
  rememberOpenChat,
} from './chatHandoff';
import { copiesForContext, RecentCopy } from './modelCopies';
import { answerText } from './answerText';
import { CopyAnswer } from './CopyAnswer';
import { ScoreCard } from './ScoreCard';
import { BaseCard, RulesCard, ThresholdsCard } from './RulesCard';
import {
  ModelPageTarget,
  baseCardFrom,
  rulesCardFrom,
  thresholdsCardFrom,
} from './rulesCardData';
import { scoreCardFrom } from './scoreCardData';
import { SignalCard } from './SignalCard';
import { signalCardFrom } from './signalCardData';
import { SplitCard } from './SplitCard';
import { splitCardFrom } from './splitCardData';
import { Plan, parsePlan, planProgress } from './planBlock';
import { Choices, parseChoices, whyHeldBack } from './choicesBlock';
import { whenReadable } from './sessionTime';
import { RemovalPreview, parseRemovalPreview } from './removalPreview';

// Blocks whose stored body differs from what streamed, because rgip merges a tool's
// computed numbers over the model's text when it persists them. For these the stream
// is a fragment and the card has to read the stored row.
const SERVER_MERGED_BLOCKS = new Set(['removal_preview']);
import { emitTreeStep } from './treeRefreshBus';
import { emitSignalsRefresh } from './signalsRefreshBus';
import {
  emitDatasetLoading,
  emitModelRefresh,
  emitRulesRefresh,
  focusRuleGroup,
} from './modelChangeBus';
import { onAsk } from './askBus';
import { useHeroHost } from './heroHost';
import { modelQuestions } from './modelQuestions';
import { emitSummaryRefresh } from './summaryRefreshBus';
import { MemoryBanner } from './MemoryBanner';
import { MemoryDrawer } from './MemoryDrawer';
import { MemoryChange } from './MemoryItem';
import { useMemory } from './useMemory';
import {
  fmtDur,
  liveStatus,
  LiveStatus,
  ToolRun,
  ToolTrail,
} from './ToolTrail';
import './aiModelSummary.css';
import './modelChatPanel.css';

// The jump kinds this panel knows how to act on. Anything else is dropped
// rather than rendered as a chip that does nothing when clicked.
const JUMP_TARGETS = new Set(['tree_node', 'model']);

interface Props {
  tenantId: string;
  modelId: string;
  /** Deployed, per the model's own `live` flag. Decides one opening question. */
  isLive?: boolean;
  /**
   * The agent this model needs, from `chatAgentFor`: the tree builder or the rules
   * builder, by the model's base. A conversation from the other one opens read-only.
   * The page mounts the panel only for a model that has an agent.
   */
  agent: string;
  /**
   * The model can be worked on: its dataset exists and is not still building. The
   * same value the model page disables its own tabs on.
   *
   * Not the same as editable, and the difference is deliberate. A LIVE model is
   * read-only and offers still belong there: the page is read-only on one too,
   * questions are the point, and a change is refused with an answer worth reading.
   * A model that is not READY allows nothing anywhere, so an offer on one is a
   * button whose only outcome is being told no.
   */
  isReady?: boolean;
  /**
   * A copy of this model is still being assembled, so the page will reload itself when
   * Studio finishes (`RootPageModel.getDuplicationStatus`).
   *
   * Two things depend on it, and `isReady` can answer neither. It is about the DATASET,
   * which takes hours, so a fresh copy held back by a duplication that takes minutes was
   * told to come back when the dataset finished building: the wrong wait, off by a
   * couple of orders of magnitude, on the one page where somebody is actively waiting.
   * And the reload is why the handoff mark has to survive this arrival.
   */
  isDuplicating?: boolean;
  /**
   * Copies of this model that already exist, sent with every turn.
   *
   * Only ever populated on a LIVE model, where every change asked for ends in the offer
   * to copy it and work there. The agent could only find these inside a write it has no
   * reason to attempt, so its first reply, the one that makes the offer, was written
   * without them and sent people off to make a second copy of a model they had already
   * copied an hour before.
   */
  modelCopies?: ReadonlyArray<RecentCopy>;
  onFocusNode: (treeId: number, nodeId: number) => void;
  /**
   * Open another model. Optional: a host that cannot navigate simply gets no
   * chip, which is the same fail-quiet an unknown target type gets.
   */
  onFocusModel?: (modelId: string) => void;
  /** Open one of this model's pages: where a change the agent just made lives. */
  onFocusPage?: (page: ModelPageTarget) => void;
  /** Sends the chat's Segment events (`chatAnalytics`). Optional: no host, no events. */
  onTrack?: ChatTracker;
}

interface Message {
  role: 'user' | 'assistant';
  // A user turn is one string. An assistant turn is an ordered list of parts, so a
  // block or a jump pill renders where the agent produced it rather than being
  // appended after the answer. See chatParts.ts.
  content?: string;
  parts?: ChatPart[];
  // The turn's tool-call trail and the model's own thinking, both streamed live.
  tools?: ToolRun[];
  reasoning?: string;
  startedAt?: number; // when the turn began (assistant)
  elapsedMs?: number; // total turn time once finished
  // The user stopped this turn. Distinct from an error: the partial answer above
  // is real, it just isn't finished, and nothing went wrong.
  stopped?: boolean;
  // The stop request itself failed, so the worker may still be running the turn.
  // Worth saying, because this agent writes to the tree.
  stopFailed?: boolean;
}

/**
 * A persisted thread message as this panel renders it.
 *
 * A user turn is a string and an assistant turn is parts, so replaying history is
 * not a cast. `getTranscript` hands back the prose plus the jump chips rebuilt
 * from the turn's persisted steps; the chips go after the text because their
 * position within the answer is not persisted, only their existence.
 *
 * The live trail (tools, reasoning, timings) is deliberately not rebuilt: it
 * describes work in flight, and a replayed turn is finished.
 */
function toPanelMessage(m: ChatMessage): Message {
  if (m.role === 'user') return { role: 'user', content: m.content };
  const parts = (m.actions ?? []).reduce(
    (acc, action) =>
      appendPill(acc, {
        targetType: action.target_type,
        targetId: action.target_id,
        label: action.label,
      }),
    appendText([], m.content)
  );
  return { role: 'assistant', parts };
}

/**
 * The stored bodies for one turn's blocks, put back as block parts.
 *
 * A transcript carries reference stubs and no content, so a replayed turn loses its
 * blocks; the one that matters is the offer the agent ends on. Arriving at the copy
 * to "open that copy and I can add the override there" as flat prose means typing it
 * out again, when the agent already wrote the two buttons.
 *
 * Resolved by `key` against `artifact_key`, which is the only stable join: a title is
 * written by the model and two chains can share one.
 *
 * Best-effort throughout. Every block that cannot be fetched simply stays absent, and
 * the prose above it already says what the turn said.
 */
async function hydrateBlocks(
  tenant: string,
  thread: string,
  refs: DeliverableRef[]
): Promise<ChatPart[]> {
  const stored = await listDeliverables(tenant, thread);
  const byKey = new Map(stored.map((d) => [d.artifact_key, d]));
  let parts: ChatPart[] = [];
  for (const ref of refs) {
    const row = byKey.get(ref.key);
    if (!row) continue;
    let raw = '';
    try {
      raw = await fetchDeliverableContent(tenant, row.deliverable_id);
    } catch {
      continue;
    }
    if (!raw) continue;
    parts = openBlock(parts, {
      key: ref.key,
      kind: ref.kind || row.kind,
      title: ref.title || row.title,
      // The stubs carry no format and the detail route's is not read here. Markdown is
      // what rgip defaults to, and the one block this exists for is `structured`, whose
      // renderer parses the body as JSON and never consults the format at all.
      format: blockFormat(undefined),
    });
    parts = appendBlockDelta(parts, ref.key, raw);
    parts = closeBlock(parts, ref.key);
    parts = bindDeliverable(parts, ref.key, row.deliverable_id);
  }
  return parts;
}

/**
 * A stored conversation as this panel renders it, or null when there is nothing to show.
 *
 * Shared by the three ways one gets opened: a duplication handing it over, the landing
 * page offering it, and the session list. They must not drift, because a conversation
 * that loses its blocks on one route and keeps them on another is worse than one that
 * never had them.
 *
 * Only the LAST turn's blocks are fetched back. Only the last turn's can be acted on
 * (`canChoose` is `isLast && !asking`), so an earlier block would draw a question with
 * dead buttons, and fetching every turn's bodies to grey them out is a request per
 * block for nothing.
 */
async function replayThread(
  tenant: string,
  threadId: string
): Promise<Message[] | null> {
  let history: ChatMessage[];
  try {
    history = await getTranscript(tenant, threadId);
  } catch {
    return null;
  }
  if (history.length === 0) return null;
  const replayed = history.map(toPanelMessage);
  const last = history[history.length - 1];
  if (last?.role !== 'assistant' || !last.deliverables?.length) return replayed;
  let blocks: ChatPart[] = [];
  try {
    blocks = await hydrateBlocks(tenant, threadId, last.deliverables);
  } catch {
    return replayed;
  }
  if (blocks.length === 0) return replayed;
  const tail = replayed[replayed.length - 1];
  replayed[replayed.length - 1] = {
    ...tail,
    parts: [...(tail.parts ?? []), ...blocks],
  };
  return replayed;
}

/**
 * A thread as the panel shows it: after a handover, the conversation it was handed over
 * from comes first, muted above the divider, the way it looked when it changed hands.
 * `carried` is how many of `messages` are that earlier conversation. Null when there is
 * nothing to show at all.
 */
async function replayHandedOver(
  tenant: string,
  threadId: string,
  rows: ThreadRow[]
): Promise<{ messages: Message[]; carried: number } | null> {
  const source = rows.find((t) => t.thread_id === threadId)?.metadata
    ?.handed_from;
  const [earlier, own] = await Promise.all([
    source ? replayThread(tenant, source) : Promise.resolve(null),
    replayThread(tenant, threadId),
  ]);
  if (!earlier && !own) return null;
  return {
    messages: [...(earlier ?? []), ...(own ?? [])],
    carried: earlier?.length ?? 0,
  };
}

// The stable, cached summary thread (keys the model_summary deliverable).
const summaryThread = (modelId: string) => `model-summary:${modelId}`;

// The `plan` block: the agent's stated intent for a long piece of work.
//
// Steps that name a tree node are links, so the plan doubles as the receipt for
// what has been done. Status glyphs are text, not colour alone: a plan read at a
// glance has to survive being screenshotted and being colour-blind.
function PlanBlock({
  plan,
  title,
  onFocus,
}: {
  plan: Plan;
  title: string;
  onFocus: (targetType: string, targetId: string) => void;
}) {
  const { settled, total } = planProgress(plan);
  return (
    <div className="mcp-plan">
      <div className="mcp-plan-head">
        <span className="mcp-plan-title">{title || 'Plan'}</span>
        <span className="mcp-plan-count">
          {settled}/{total}
        </span>
      </div>
      {plan.goal && <p className="mcp-plan-goal">{plan.goal}</p>}
      <ol className="mcp-plan-steps">
        {plan.steps.map((step, i) => {
          const linked = step.tree != null && step.node != null;
          return (
            <li
              key={i}
              className={`mcp-plan-step mcp-plan-step--${step.status}`}
            >
              <span className="mcp-plan-mark" aria-hidden>
                {step.status === 'done'
                  ? '✓'
                  : step.status === 'skipped'
                  ? '–'
                  : step.status === 'running'
                  ? '›'
                  : '○'}
              </span>
              <span className="mcp-plan-sr">{step.status}</span>
              {linked ? (
                <button
                  type="button"
                  className="mcp-plan-link"
                  onClick={() =>
                    onFocus('tree_node', `${step.tree}:${step.node}`)
                  }
                >
                  {step.text}
                </button>
              ) : (
                <span className="mcp-plan-text">{step.text}</span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// The `choices` block: a decision handed back to the user.
//
// Pressing one sends its `message` as an ordinary turn, so the agent hears an
// instruction rather than the UI performing an action. Once something is picked the
// block goes inert and says what was picked: a second press would send a conflicting
// instruction about a tree that has already moved.
function RemovalPreviewCard({
  preview,
  onChoose,
  answeredKey,
}: {
  preview: RemovalPreview;
  onChoose?: (message: string) => void;
  answeredKey?: string;
}) {
  const { wouldDestroyNodes, populationReturning, conditionParts } = preview;
  // The cut itself in the header slot, the way a split card leads with its condition.
  // A compound cut names its size instead of printing five conditions on one line:
  // the list below is where they are readable.
  const cut = preview.isCompound
    ? `${conditionParts.length} conditions in one cut`
    : conditionParts[0] || 'this split';
  return (
    <div className="mrm">
      <div className="mrm-hd">
        <div className="mrm-slot">
          <span className="mrm-where">Removing</span>
          <span className="mrm-cut">{cut}</span>
        </div>
        <div className="mrm-nums">
          {/* Amber on this one only. It is the "argues against" idea the signal card
              already uses that hue for, and the rest of the card stays neutral
              because on this page colour names a fit tier. */}
          <div className="mrm-num mrm-num--against">
            <b>{wouldDestroyNodes.toLocaleString()}</b>
            <span>{wouldDestroyNodes === 1 ? 'node goes' : 'nodes go'}</span>
          </div>
          {populationReturning !== null && (
            <div className="mrm-num">
              <b>{populationReturning.toLocaleString()}</b>
              <span>leads merge</span>
            </div>
          )}
        </div>
      </div>

      {(preview.headline || conditionParts.length > 0) && (
        <div className="mrm-body">
          {preview.headline && <p className="mrm-why">{preview.headline}</p>}
          {conditionParts.length > 0 && (
            <ul className="mrm-parts">
              {conditionParts.map((part) => (
                <li key={part}>{part}</li>
              ))}
            </ul>
          )}
          {preview.isCompound && (
            <p className="mrm-note">
              All of them go. Replacing the cut keeps every branch below it.
            </p>
          )}
        </div>
      )}

      {preview.choices && (
        <div className="mrm-foot">
          <ChoicesBlock
            choices={preview.choices}
            onChoose={onChoose}
            answeredKey={answeredKey}
          />
        </div>
      )}
    </div>
  );
}

function ChoicesBlock({
  choices,
  onChoose,
  answeredKey,
  heldBack,
}: {
  choices: Choices;
  onChoose?: (message: string) => void;
  answeredKey?: string;
  /**
   * Why these cannot be pressed right now, when that is a state the reader could
   * wait out rather than a turn that is simply over.
   *
   * A disabled button with no reason reads as broken. This one arrives at the end of
   * a duplication, on a page saying the dataset may take hours, so "nothing happens
   * when I click" is the least useful thing it could say.
   *
   * It is rendered twice, and the `<p className="mcp-choices-held">` below the options
   * is the one the reader gets. A `title` tooltip does not open on a disabled control,
   * and every option here is disabled exactly when there is a reason to show, so a
   * caveat placed only on the title would never be seen by the person it is for. The
   * title is kept because it is still the attribute on the control itself and it costs
   * a word, but it is not the mechanism.
   */
  heldBack?: string;
}) {
  const settled = answeredKey !== undefined || !onChoose;
  return (
    <div className="mcp-choices">
      {choices.question && <p className="mcp-choices-q">{choices.question}</p>}
      <div className="mcp-choices-opts">
        {choices.options.map((option) => {
          const picked = answeredKey === option.label;
          return (
            <button
              type="button"
              key={option.label}
              className={`mcp-choice${picked ? ' mcp-choice--picked' : ''}`}
              disabled={settled}
              title={settled ? heldBack : undefined}
              onClick={() => onChoose?.(option.message)}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {heldBack && settled && <p className="mcp-choices-held">{heldBack}</p>}
    </div>
  );
}

// One part of an assistant turn, rendered where the agent produced it.
//
// A block is dispatched on its `key`, which is the agent's own name for the thing
// (`plan`, `choices`, ...), not on rgip's `kind` vocabulary — `kind` is a closed set
// about persistence (document / email / structured / ...) and says nothing about how
// a chat should draw it.
//
// The default branch is the important one: an unrecognised key renders its body as
// markdown rather than nothing. rgip's `inbound-model-builder` currently sets
// `deliverables: false` precisely because this panel dropped blocks on the floor, so
// a block reached the user as "here is an exec-ready version…" followed by silence.
// With a visible fallback, turning blocks back on cannot reproduce that, and a block
// type we have not styled yet is merely plain instead of missing.
function TurnPart({
  part,
  onFocus,
  onChoose,
  answeredKey,
  heldBack,
}: {
  part: ChatPart;
  onFocus: (targetType: string, targetId: string) => void;
  /** Send a choice as the user's next turn. Absent on a replayed turn. */
  onChoose?: (message: string) => void;
  /** What was already picked from this block, when something was. */
  answeredKey?: string;
  /** Why a live block cannot be pressed yet, when that is a state to wait out. */
  heldBack?: string;
}) {
  if (part.kind === 'text') {
    if (!part.text.trim()) return null;
    return (
      <div className="mcp-bubble mcp-bubble--assistant mcp-md">
        <Markdown text={part.text} onFocus={onFocus} />
      </div>
    );
  }

  if (part.kind === 'card') {
    return (
      <SplitCard
        card={part.card}
        index={part.splitIndex}
        defaultOpen={part.splitIndex === 1}
        onFocus={onFocus}
      />
    );
  }

  if (part.kind === 'scoreCard') {
    return <ScoreCard card={part.card} onFocus={onFocus} />;
  }

  if (part.kind === 'signalCard') {
    return (
      <SignalCard
        card={part.card}
        onOpen={(target) => onFocus('page', target)}
      />
    );
  }

  if (part.kind === 'rulesCard') {
    return (
      <RulesCard
        card={part.card}
        onOpen={(target) => onFocus('page', target)}
      />
    );
  }

  if (part.kind === 'thresholdsCard') {
    return (
      <ThresholdsCard
        comparison={part.comparison}
        onOpen={(target) => onFocus('page', target)}
      />
    );
  }

  if (part.kind === 'baseCard') {
    return <BaseCard card={part.card} />;
  }

  if (part.kind === 'pill') {
    return (
      <button
        type="button"
        className="mcp-jump"
        onClick={() => onFocus(part.targetType, part.targetId)}
      >
        {part.label || 'Open in Studio'}
      </button>
    );
  }

  // A key with a renderer of its own. Parsing that fails returns null and drops
  // through to the markdown fallback below, so a malformed plan is readable rather
  // than missing — the same reason the fallback exists at all.
  if (part.key === 'plan') {
    const plan = parsePlan(part.raw);
    if (plan)
      return <PlanBlock plan={plan} title={part.title} onFocus={onFocus} />;
  }

  // Before `choices`, because this block carries its own options and renders them
  // inside the card: two separate button strips for one decision is worse than none.
  if (part.key === 'removal_preview') {
    const preview = parseRemovalPreview(part.raw);
    if (preview)
      return (
        <RemovalPreviewCard
          preview={preview}
          onChoose={onChoose}
          answeredKey={answeredKey}
        />
      );
    // Deliberately NOT the markdown fallback. This block's body is the model's
    // fragment until the stored one is fetched, and the fallback would print that
    // JSON at the reader. A quiet placeholder for the beat in between.
    return (
      <div className="mcp-removal-wait">Working out what this would take…</div>
    );
  }

  if (part.key === 'choices') {
    const choices = parseChoices(part.raw);
    // Only offer the buttons while the block can still be acted on. A block whose
    // turn has ended (no `onChoose`) renders its question and what was chosen, so a
    // scrolled-back conversation reads as a record rather than as live buttons over
    // a tree that has since moved.
    if (choices)
      return (
        <ChoicesBlock
          choices={choices}
          onChoose={onChoose}
          answeredKey={answeredKey}
          heldBack={heldBack}
        />
      );
  }

  // An html block is a self-contained document (rgip applies the style server-side),
  // so it goes in a sandboxed iframe with scripts off: report CSS must not leak into
  // Studio. Same treatment the report cards already use.
  const body =
    part.format === 'html' ? (
      <iframe
        className="mcp-block-frame"
        title={part.title || 'Report'}
        sandbox=""
        srcDoc={part.raw}
      />
    ) : (
      <div className="mcp-md">
        <Markdown text={part.raw} onFocus={onFocus} />
      </div>
    );

  // A titled block folds. These run to a full report inside a 360px column, and one
  // read once should not push the rest of the conversation off screen forever.
  // Untitled blocks do not, because there would be nothing to label the control
  // with, and folding behind the word "Deliverable" tells the reader nothing.
  //
  // `details`/`summary` for the keyboard and screen-reader behaviour, with the
  // marker suppressed: a disclosure triangle reads cheap on a panel like this. Open
  // by default, since a block the agent just wrote is the thing being looked at.
  if (!part.title) return <div className="mcp-block">{body}</div>;
  return (
    <details className="mcp-block" open>
      <summary className="mcp-block-title">
        <span>{part.title}</span>
        {/* Still streaming. Without it a long block looks finished from the moment
            its first line lands, and a reader takes a half-written report as whole. */}
        {!part.done && (
          <span className="mcp-tool-spinner" aria-label="writing" />
        )}
      </summary>
      {body}
    </details>
  );
}

export function ModelChatPanel({
  tenantId,
  modelId,
  isLive,
  agent,
  // Defaulted true so a host that does not know stays exactly as it was.
  isReady = true,
  isDuplicating = false,
  modelCopies,
  onFocusNode,
  onFocusModel,
  onFocusPage,
  onTrack,
}: Props) {
  // Docked when the responsive shell is on: it reserves a real grid column and
  // this portals into it, so the panel is laid out with the page rather than
  // floating over it, and the shell can make room by folding the nav rail.
  //
  // Read from the flag, not from whether the host element happens to exist. The
  // host comes and goes as the shell opens and closes the column, and keying off
  // it made a closed column look briefly like "no shell", rendering the floating
  // panel underneath.
  // The AI flag, not the layout one: the column exists wherever the chat does.
  const shellHosted = useShellPanelHost();
  // A slot offered by the page on screen (the Overview). It WINS over the rail: a page
  // that asks for the chat inline is a page whose whole subject is the conversation, and
  // drawing it in both places at once is what the Overview looked like before this.
  const heroEl = useHeroHost();
  const onPage = heroEl !== null;
  const shellPanel = useSidePanelStore();
  // The shell cannot reserve a column and then discover whether anything wants
  // it: the occupant renders far below and needs the host to already exist. So
  // the occupant announces itself and the column follows.
  const { setRequested, setRailed } = shellPanel;
  // null = still resolving the flag; false = feature off → render nothing.
  const [enabled, setEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    // Not while it is on the page: the column would be reserved, and paid for in width,
    // for a panel that is rendering somewhere else entirely.
    setRequested(shellHosted && enabled === true && !onPage);
    return () => setRequested(false);
  }, [shellHosted, enabled, onPage, setRequested]);

  // The panel is fixed to the viewport so it never scrolls with the page. It must
  // sit exactly over the shell's <main> (below the top bar and the optional
  // org-context banner), so measure <main>'s box rather than hard-coding those
  // heights — the banner's presence changes them. Re-measured on resize and once
  // the flag resolves (the row it docks beside only exists then).
  const [box, setBox] = useState<{ top: number; height: number }>({
    top: 64,
    height: 0,
  });
  useLayoutEffect(() => {
    const measure = () => {
      const main = document.querySelector('main');
      if (!main) return;
      const r = main.getBoundingClientRect();
      setBox({ top: r.top, height: r.height });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [enabled]);

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [asking, setAsking] = useState(false);
  // A clock ticked while a turn runs, so in-flight tool durations and the turn
  // timer update live (admin's chat does the same).
  const [now, setNow] = useState(0);
  // The cached summary, fetched once per model, used only to seed the agent's context.
  const [summary, setSummary] = useState<ModelSummary | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  // The scroll region, plus whether to keep following it. Held in refs because the
  // scroll handler must not re-render the panel: it fires on every wheel tick.
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickRef = useRef(true);
  // The live run, so Stop can halt the work rather than only detach from it, and a
  // flag telling the turn's own catch that the abort it is seeing was deliberate.
  const runIdRef = useRef<string | null>(null);
  const stoppedRef = useRef(false);
  // Read through a ref so a host passing an inline arrow does not rebuild `ask`
  // on every render.
  const trackRef = useRef(onTrack);
  trackRef.current = onTrack;
  // A copy this turn made, to open once the turn is over. Held rather than acted on the
  // moment it arrives: the reply is still streaming then, and navigating would cut the
  // sentence that says what was made.
  const modelJumpRef = useRef<string | null>(null);
  const [stopping, setStopping] = useState(false);
  // Which option was pressed, per turn index. Local to this tab: the pick reaches
  // the server as an ordinary message, so the transcript records the instruction
  // rather than the button, and this only keeps the block from being pressed twice.
  const [picked, setPicked] = useState<Record<number, string>>({});

  // A fresh conversation per mount, reset on model change — no shared history
  // across visits or users (matches the old ask box).
  //
  // The one exception is a conversation handed here from the model this one was
  // duplicated from; `adoptedThread` carries it and is claimed in an effect below,
  // because the claim reads sessionStorage and this renders on the server too.
  const followupRef = useRef<{ modelId: string; thread: string }>();
  if (followupRef.current?.modelId !== modelId) {
    followupRef.current = {
      modelId,
      thread: `model-chat:${modelId}:${crypto.randomUUID()}`,
    };
  }
  const [adoptedThread, setAdoptedThread] = useState<string | null>(null);
  // The reader asked for a clean slate on a model that already has a conversation, so
  // resuming must not undo that. Reset on a model change: it is a choice about this
  // conversation, not a standing preference to never resume anything again.
  const [startedFresh, setStartedFresh] = useState(false);
  // This person's earlier conversations about this model, newest first. Loaded on every
  // visit because the landing page offers them; only opened when they ask.
  const [sessions, setSessions] = useState<ThreadRow[]>([]);
  const [showSessions, setShowSessions] = useState(false);
  // The assistant's Memory, behind the AI flag (`studio-ai-model-insights`). While that
  // is off, `memoryOn` stays false, `showMemory` can never become true, and every
  // condition below that mentions either reads exactly as it did before Memory existed.
  const [memoryOn, setMemoryOn] = useState(false);
  // The flag read in flight: a turn sent before it settles waits for it, so the first
  // message of a session does not go out without `memory` just because it was quick.
  const memoryFlag = useRef<Promise<boolean>>(Promise.resolve(false));
  const [showMemory, setShowMemory] = useState(false);
  // Bumped when the list may have changed under us: a turn ended (the agent proposes
  // during one) or the docked panel was opened again.
  const [memoryTick, setMemoryTick] = useState(0);
  const [memoryNotice, setMemoryNotice] = useState<string | null>(null);
  const {
    list: memoryList,
    error: memoryError,
    reload: reloadMemory,
  } = useMemory(tenantId, modelId, memoryOn, memoryTick);
  const followupThread = adoptedThread ?? followupRef.current.thread;
  // The agent this model needs now, and the one the open conversation ran under. They
  // differ after the base is switched, or when an older conversation from the other
  // base is opened. A conversation that has not had a turn yet belongs to nobody.
  const [threadAgent, setThreadAgent] = useState<string | null>(null);
  // Read-only rather than continued: the old thread's history holds the other agent's
  // tool calls, and that agent is not the one that can work on the model as it is now.
  // "Continue here" carries the conversation over instead (rgip's handoff).
  const readOnly =
    messages.length > 0 && threadAgent !== null && threadAgent !== agent;
  const [handingOff, setHandingOff] = useState(false);
  // The base was switched by a turn in THIS conversation. The reader already said yes to
  // the switch, so the conversation moves to the other agent on its own once the model
  // is re-read, rather than asking a second time with a button.
  const switchedHereRef = useRef(false);
  // How many of `messages` belong to the conversation this one was handed over from.
  // They stay on screen, muted, above a divider, so the handover reads as the same
  // conversation changing hands rather than as the panel resetting. Only this session
  // has them: a reload reopens the new thread on its own.
  const [carriedCount, setCarriedCount] = useState(0);

  // An unknown target type is ignored rather than guessed at, which is the
  // documented extension point for new jump kinds. `model` is the second one:
  // the agent duplicates a live model and the work continues on the copy.
  //
  // Nothing is carried across the navigation. The turn that made the copy re-pointed
  // this conversation onto it server-side, so the copy's page finds it by asking for
  // this person's threads about that model, the same way every page does.
  const focusFromPill = useCallback(
    (targetType: string, targetId: string) => {
      if (targetType === 'model') {
        // Deliberate: the work moved to the copy, so the conversation goes with it.
        markHandoff(targetId);
        onFocusModel?.(targetId);
        return;
      }
      if (targetType === 'page') {
        onFocusPage?.(targetId as ModelPageTarget);
        return;
      }
      if (targetType === 'rule_group') {
        // `studio:rule_group/2` in the agent's prose: the Rules page, with group 2 open.
        const group = Number(targetId);
        if (!Number.isInteger(group) || group < 1) return;
        focusRuleGroup(group);
        onFocusPage?.('rules');
        return;
      }
      if (targetType !== 'tree_node') return;
      const [treeId, nodeId] = targetId.split(':').map(Number);
      if (Number.isFinite(treeId) && Number.isFinite(nodeId))
        onFocusNode(treeId, nodeId);
    },
    [onFocusNode, onFocusModel, onFocusPage]
  );

  // Leave the resumed conversation and open an empty one.
  //
  // A new id rather than clearing the old thread: the old one is a real conversation
  // somebody may want back, and rgip keeps it. Nothing is deleted here.
  //
  // `startedFresh` is what stops the resume effect from immediately pulling the old
  // conversation back in, since the new thread has no row until its first turn lands.
  const startNewChat = useCallback(() => {
    abortRef.current?.abort();
    followupRef.current = {
      modelId,
      thread: `model-chat:${modelId}:${crypto.randomUUID()}`,
    };
    setAdoptedThread(null);
    setStartedFresh(true);
    setMessages([]);
    setPicked({});
    setThreadAgent(null);
    setCarriedCount(0);
    forgetOpenChat();
  }, [modelId]);

  // Carry a conversation from the other base over to the agent this model needs now.
  // rgip starts a new thread seeded with a brief of this one and leaves this one as it
  // was, so it stays in the session list under its own label.
  const continueHere = useCallback(async () => {
    if (!readOnly || handingOff) return;
    setHandingOff(true);
    switchedHereRef.current = false;
    try {
      const next = await handoffThread(tenantId, followupThread, agent);
      setAdoptedThread(next);
      setThreadAgent(agent);
      setCarriedCount(messages.length);
      setStartedFresh(false);
      setSessions(await listModelThreads(tenantId, modelId));
    } catch {
      // The conversation stays readable where it is; the button can be pressed again.
    } finally {
      setHandingOff(false);
    }
  }, [
    readOnly,
    handingOff,
    messages.length,
    tenantId,
    followupThread,
    agent,
    modelId,
  ]);

  // The switch happened in this conversation: carry it over as soon as the turn that made
  // it has finished, so the brief includes the agent's last answer.
  useEffect(() => {
    if (!readOnly || asking || !switchedHereRef.current) return;
    continueHere().catch(() => undefined);
  }, [readOnly, asking, continueHere]);

  // Open one of the earlier conversations, from the list or from the landing page.
  const openSession = useCallback(
    (threadId: string) => {
      setShowSessions(false);
      setStartedFresh(false);
      setCarriedCount(0);
      setAdoptedThread(threadId);
      setThreadAgent(
        sessions.find((t) => t.thread_id === threadId)?.agent ?? TREES_AGENT
      );
      setMessages([]);
      setPicked({});
      void replayHandedOver(tenantId, threadId, sessions).then((replayed) => {
        if (!replayed) return;
        setMessages(replayed.messages);
        setCarriedCount(replayed.carried);
      });
    },
    [tenantId, sessions]
  );

  // Resolve the per-tenant feature flag first; everything else waits on it.
  useEffect(() => {
    let cancelled = false;
    fetchAiInsightsEnabled(tenantId)
      .then((e) => !cancelled && setEnabled(e))
      .catch(() => !cancelled && setEnabled(false));
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  /**
   * The subject this conversation belongs to, as of the last time it was cleared.
   *
   * Seeded with the subject at MOUNT so the first resolve of `enabled` is not itself
   * treated as a change. `enabled` arrives asynchronously, so the effect below runs a
   * second time a beat after mount, and clearing there wiped any conversation that had
   * started in between: a question handed over from the models list ran, persisted,
   * and then vanished from the panel, which then showed its empty state as though
   * nothing had been asked.
   */
  const conversationFor = useRef(`${tenantId}:${modelId}`);

  // New model → clear the conversation and re-read the cached summary for context.
  // Best-effort: a missing summary just means the follow-up runs without seeding.
  useEffect(() => {
    if (enabled !== true) return undefined;
    let cancelled = false;
    const subject = `${tenantId}:${modelId}`;
    // Only a real change of subject clears. A flag resolving is not one.
    if (conversationFor.current !== subject) {
      conversationFor.current = subject;
      setMessages([]);
      setPicked({});
      setSummary(null);
      setThreadAgent(null);
      setCarriedCount(0);
      switchedHereRef.current = false;
      // Inside the guard with the rest: asking for a clean slate is a choice about
      // this conversation, and a flag resolving underneath must not undo it.
      setStartedFresh(false);
    }
    // Tree models only. The stored summary describes the trees, and a rule based
    // model's trees are stored but score nothing, so seeding the rules agent with it
    // would hand it a confident account of a model that is not the one scoring.
    if (agent !== TREES_AGENT) {
      setSummary(null);
      return () => {
        cancelled = true;
      };
    }
    fetchModelSummary(tenantId, summaryThread(modelId))
      .then((s) => !cancelled && setSummary(s?.summary ?? null))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [tenantId, modelId, enabled, agent]);

  // Pick this person's conversation about this model back up: on the copy the
  // conversation moved to when it was made, and across the reload that copy's own page
  // performs when the duplication finishes. The index is the server's
  // (GET /v1/threads?mine&model_id), so it survives the tab and the device rather than
  // living in browser storage.
  //
  // That reload is the whole reason `spend` exists. The mark saying "this arrival is a
  // handoff" used to be taken by the first arrival, so the second one, the reload,
  // found nothing: the conversation appeared on the copy and then emptied itself the
  // moment Studio finished, which is exactly the moment somebody is watching it. While
  // a duplication is in progress the mark is read and left in place.
  //
  // `isDuplicating` is deliberately NOT a dependency below. What it answers here is
  // whether THIS arrival is the last one, which was settled before the page rendered;
  // re-running on a later flip would re-list the threads and replace the transcript
  // under a conversation somebody is in the middle of.
  //
  // Runs after the reset above (declaration order), so the clear happens first and
  // this fills the panel back in. Both the thread and the transcript are needed and
  // they answer different halves: the thread is what gives the AGENT its history,
  // the transcript is what gives the READER theirs. Adopting the thread without the
  // transcript is the worse failure of the two, because the panel then looks empty
  // while the agent answers as though mid-conversation.
  //
  // Best-effort on the transcript alone: if it cannot be read, keep the thread. A
  // blank panel over a live conversation is recoverable, a lost conversation is not.
  const recalledRef = useRef(false);
  useEffect(() => {
    if (enabled !== true) return undefined;
    if (startedFresh) return undefined;
    let cancelled = false;
    const resume = async () => {
      const threads = await listModelThreads(tenantId, modelId);
      if (cancelled) return;
      setSessions(threads);
      // Offered, not reopened. Arriving at a model is a reason to ask about the model,
      // not to be dropped into the middle of something that may have finished days ago.
      // The one exception is a navigation the app made on the reader's behalf: after a
      // duplication the work continues on the copy, so landing there empty would strand
      // every decision behind them.
      //
      // A reload is not an arrival: the conversation that was open comes back, exactly
      // that one, whichever assistant it belongs to. Read on the first pass only, because
      // this effect runs again when the base changes, and the open conversation is then
      // the one on screen, not one to fetch.
      const reopen = recalledRef.current ? null : recallOpenChat(modelId);
      recalledRef.current = true;
      if (reopen) {
        setAdoptedThread(reopen);
        setThreadAgent(
          threads.find((t) => t.thread_id === reopen)?.agent ?? TREES_AGENT
        );
        await replay(reopen, threads);
        return;
      }
      if (!claimHandoff(modelId, { spend: !isDuplicating })) return;
      // The newest conversation this model's CURRENT agent had. One from before a base
      // switch stays in the list, and opens read-only when picked. A row older than the
      // agent column reads as the tree builder's, which is the only agent there was.
      const remembered = threads.find(
        (t) => (t.agent ?? TREES_AGENT) === agent
      )?.thread_id;
      if (!remembered) return;
      setAdoptedThread(remembered);
      setThreadAgent(agent);
      await replay(remembered, threads);
    };
    const replay = async (remembered: string, threads: ThreadRow[]) => {
      const replayed = await replayHandedOver(tenantId, remembered, threads);
      if (cancelled || !replayed) return;
      setMessages(replayed.messages);
      setCarriedCount(replayed.carried);
    };
    void resume();
    return () => {
      cancelled = true;
    };
  }, [tenantId, modelId, enabled, startedFresh, agent]);

  // Keep the tab's note of the open conversation current, for a reload to find. Only once
  // something is on screen: a minted id with no turn behind it has nothing to reopen.
  // Cleared on leaving the model, which a reload never runs, so an arrival finds nothing.
  //
  // Skipped in the commit where the model changes. The panel is not remounted per model
  // (a duplication moves it to the copy), and in that commit `messages` still holds the
  // previous model's conversation, which would be noted down against the new one.
  const notedFor = useRef(modelId);
  useEffect(() => {
    if (notedFor.current !== modelId) {
      notedFor.current = modelId;
      return;
    }
    if (messages.length > 0) rememberOpenChat(modelId, followupThread);
  }, [modelId, followupThread, messages.length]);
  useEffect(() => () => forgetOpenChat(), [modelId]);

  // The list is written BY the turn, so the copy fetched on arrival is always one
  // turn out of date: the conversation you are currently having is missing from it,
  // and "My sessions" stayed hidden through the whole of a first conversation and
  // appeared on the next page load. Refetch when a turn ends, which is the moment
  // the row it lists comes into existence.
  //
  // Guarded on a turn having actually run, so arriving at the panel does not fetch
  // the same list the resume effect above just fetched.
  const hasAsked = useRef(false);
  useEffect(() => {
    if (asking) {
      hasAsked.current = true;
      return undefined;
    }
    if (enabled !== true || !hasAsked.current) return undefined;
    let cancelled = false;
    void listModelThreads(tenantId, modelId).then((threads) => {
      if (!cancelled) setSessions(threads);
    });
    return () => {
      cancelled = true;
    };
  }, [asking, enabled, tenantId, modelId]);

  // Memory waits on the chat's own flag: no chat, no memory.
  useEffect(() => {
    if (enabled !== true) return undefined;
    let cancelled = false;
    memoryFlag.current = fetchMemoryEnabled(tenantId).catch(() => false);
    memoryFlag.current.then((on) => !cancelled && setMemoryOn(on));
    return () => {
      cancelled = true;
    };
  }, [tenantId, enabled]);

  // Proposals arrive during a turn (`memory_propose`), so re-read once one ends. Declared
  // after the effect that sets `hasAsked`, which this reads.
  useEffect(() => {
    if (!asking && hasAsked.current && memoryOn) setMemoryTick((t) => t + 1);
  }, [asking, memoryOn]);

  // And when the docked panel is opened again: a session review may have proposed
  // something while it was folded away.
  const docked = shellPanel.hostEl !== null;
  useEffect(() => {
    if (docked) setMemoryTick((t) => t + 1);
  }, [docked]);

  // The notice is about the change just made, so it goes with the view it was made in.
  useEffect(() => setMemoryNotice(null), [showMemory, modelId]);

  const onMemoryChanged = useCallback(
    (change: MemoryChange) => {
      reloadMemory();
      // The notes block is rgip's projection of this model's approved entries, and the
      // notes card reads `activeModel.notes`: re-read the model so it shows the change.
      if (change.affectsNotes && change.entry.model_id === modelId)
        emitModelRefresh();
      setMemoryNotice(
        change.notice ??
          (change.notesSynced === false
            ? 'Saved. The model notes did not update this time.'
            : null)
      );
    },
    [reloadMemory, modelId]
  );

  // Detach any live stream on unmount / model change (the run keeps going + persists).
  useEffect(() => () => abortRef.current?.abort(), [modelId]);

  // Tick while a turn runs so live durations count up.
  useEffect(() => {
    if (!asking) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [asking]);

  // Note what the reader is doing, so the effect below knows whether it is allowed
  // to move them. Cheap enough to run on every scroll event: it reads three numbers
  // and writes a ref.
  const onScroll = useCallback(() => {
    const region = scrollRef.current;
    if (region) stickRef.current = shouldStickToBottom(region);
  }, []);

  // Follow new content only while the reader is already at the end.
  //
  // This used to depend on `now` as well, which is a clock ticking every 500ms for
  // the live durations, so the panel yanked itself to the bottom twice a second for
  // the whole turn and scrolling up to re-read anything was impossible. Turns here
  // run for minutes, so that is most of the time someone spends in this panel.
  // block:'nearest' scrolls only the panel's own region, never the page behind it.
  useEffect(() => {
    if (!stickRef.current) return;
    bottomRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [messages]);

  // Mark the turn stopped but not cleanly: the stop request itself did not land, so
  // the worker may still be running. Reached from two places, hence its own callback.
  const patchStopFailed = useCallback(() => {
    setMessages((prev) =>
      prev.map((m, i) =>
        i === prev.length - 1 && m.role === 'assistant'
          ? { ...m, stopped: true, stopFailed: true }
          : m
      )
    );
  }, []);

  // The chat asked to regenerate the top-of-page summary (refresh_summary tool).
  // Hard refresh: clear the stable summary thread first so the agent regenerates
  // from a fresh tool call, then tell the Overview card to re-fetch. Best-effort.
  const refreshSummary = useCallback(async () => {
    const thread = summaryThread(modelId);
    try {
      await resetThread(tenantId, thread);
      const runId = await startTurn(
        tenantId,
        thread,
        modelId,
        'Summarize this model.',
        { agent: 'model-summary' }
      );
      await streamRun(tenantId, runId, {});
      emitSummaryRefresh();
    } catch {
      // The card keeps its cached summary; nothing to surface in the chat.
    }
  }, [tenantId, modelId]);

  const ask = useCallback(
    async (override?: string, opts: { suggested?: boolean } = {}) => {
      const question = (override ?? input).trim();
      if (!question || asking || readOnly) return;
      setThreadAgent(agent);
      setInput('');
      setAsking(true);
      setNow(Date.now());
      // Asking is an explicit request to watch the answer, so re-arm the follow even
      // if the reader had scrolled away during the previous turn.
      stickRef.current = true;
      stoppedRef.current = false;
      setStopping(false);
      setMessages((m) => [
        ...m,
        { role: 'user', content: question },
        { role: 'assistant', parts: [], tools: [], startedAt: Date.now() },
      ]);
      const patchLast = (fn: (m: Message) => Message) =>
        setMessages((prev) =>
          prev.map((m, i) => (i === prev.length - 1 ? fn(m) : m))
        );
      // A finished write leaves a receipt in the transcript, where it happened: what
      // was applied, and a link to the node now carrying it. The plan states the
      // intent; these are what actually landed.
      const receiptFor = (e: ChatActivityEvent) => {
        // A write that carries a card draws the card INSTEAD of the pill: the pill is
        // the same fact in one line, and showing both says everything twice.
        // The receipt is NESTED under `receipt`, never flattened onto the event: the
        // activity's own target is the tree to refetch, the receipt's is the node that
        // now holds the split. Reading `detail` off the event finds nothing.
        const receipt = e.receipt;
        if (!receipt) return;
        // A dataset load the agent started. The load runs server side and the page
        // would go on showing the old dataset until someone reloads, so tell it.
        if (receipt.target_type === 'dataset') {
          if (String(receipt.target_id) === String(modelId))
            emitDatasetLoading();
          return;
        }
        const card = splitCardFrom(receipt);
        if (card) {
          patchLast((m) => ({ ...m, parts: appendCard(m.parts ?? [], card) }));
          return;
        }
        const scored = scoreCardFrom(receipt);
        if (scored) {
          patchLast((m) => ({
            ...m,
            parts: appendScoreCard(m.parts ?? [], scored),
          }));
          return;
        }
        const signals = signalCardFrom(receipt);
        if (signals) {
          patchLast((m) => ({
            ...m,
            parts: appendSignalCard(m.parts ?? [], signals),
          }));
          return;
        }
        const tiers = thresholdsCardFrom(receipt);
        if (tiers) {
          patchLast((m) => ({
            ...m,
            parts: appendThresholdsCard(m.parts ?? [], tiers),
          }));
          return;
        }
        const switched = baseCardFrom(receipt);
        if (switched) switchedHereRef.current = true;
        const changed = rulesCardFrom(receipt) ?? switched;
        if (changed) {
          patchLast((m) => ({
            ...m,
            parts: appendRulesCard(m.parts ?? [], changed),
          }));
          return;
        }
        if (
          !receipt.detail ||
          !JUMP_TARGETS.has(receipt.target_type ?? '') ||
          !receipt.target_id
        )
          return;
        const targetType = receipt.target_type;
        const targetId = receipt.target_id;
        const label = receipt.detail;
        // Noted here rather than read back off the messages at the end: a state updater
        // is not a place to navigate from, and this is the moment the target is known.
        if (targetType === 'model' && String(targetId) !== String(modelId)) {
          modelJumpRef.current = String(targetId);
        }
        patchLast((m) => ({
          ...m,
          parts: appendPill(m.parts ?? [], { targetType, targetId, label }),
        }));
      };

      // Close the most recent still-open tool with this id (admin's matching rule:
      // by id when the event carries one, else the last open entry).
      const endTool = (e: ChatActivityEvent) =>
        patchLast((m) => {
          const tools = [...(m.tools ?? [])];
          for (let i = tools.length - 1; i >= 0; i--) {
            const match = e.id ? tools[i].id === e.id : true;
            if (match && tools[i].endedAt == null) {
              tools[i] = { ...tools[i], endedAt: Date.now() };
              break;
            }
          }
          return { ...m, tools };
        });
      // Whether this turn got all the way to its terminal frame. It decides whether
      // the page may move afterwards, so an error keeps the reader where the error
      // is, and it is the turn's outcome in analytics.
      let failed = false;
      // The reader was detached without a stop: the panel unmounted or moved to
      // another model mid-turn (the effect that aborts on `modelId`). The run carries
      // on server side, so analytics counts it as aborted, not as an error.
      let detached = false;
      const tracked = trackTurn(
        trackRef.current,
        {
          tenantId,
          modelId,
          agent,
          sessionId: followupThread,
          surface: 'model_page',
        },
        question
      );
      try {
        const runId = await startTurn(
          tenantId,
          followupThread,
          modelId,
          question,
          {
            agent,
            memory: await memoryFlag.current,
            origin: opts.suggested ? SUGGESTED_STEP : undefined,
            context: {
              // Seed the follow-up agent with the cached summary as context. Only ever
              // set on a tree model: see the effect that fetches it.
              ...(summary && agent === TREES_AGENT
                ? { model_summary: summary }
                : {}),
              // Copies of this model that already exist, so the offer to copy it can
              // name them in the reply that MAKES the offer. The agent's own lookup
              // sits inside a write it never attempts on a live model, so that first
              // reply was written blind. Aged here rather than on the page, because the
              // panel outlives the page load by however long the tab stays open.
              //
              // The key is omitted entirely when there is nothing to send, rather than
              // sent empty: an empty list is a fact the agent would have to be told to
              // ignore, and this costs nothing on every model that is not live.
              ...(modelCopies?.length
                ? { model_copies: copiesForContext(modelCopies) }
                : {}),
            },
          }
        );
        runIdRef.current = runId;
        tracked.ran(runId);
        // Stopped while the turn was still being enqueued. At that point there was no
        // run id to cancel and no controller to abort, so `stop` could do neither and
        // the turn would otherwise stream on regardless. Cancel it here instead.
        if (stoppedRef.current) {
          await cancelRun(tenantId, runId).catch(() => patchStopFailed());
          patchLast((m) => ({ ...m, stopped: true }));
          return;
        }
        const controller = new AbortController();
        abortRef.current = controller;
        await streamRun(
          tenantId,
          runId,
          {
            onToken: (delta) =>
              patchLast((m) => ({
                ...m,
                parts: appendText(m.parts ?? [], delta),
              })),
            // The model's own thinking, streamed ahead of the answer. Shown live
            // while the turn runs, then collapsed behind a "Thinking" toggle.
            onReasoning: (delta) =>
              patchLast((m) => ({
                ...m,
                reasoning: (m.reasoning ?? '') + delta,
              })),
            onActivityStart: (e: ChatActivityEvent) => {
              // A jump becomes a pill in the answer where it arrived, not a row in
              // the trail and not a chip appended after the whole turn.
              const jump = jumpFromActivity(e);
              if (jump) {
                patchLast((m) => ({
                  ...m,
                  parts: appendPill(m.parts ?? [], {
                    targetType: jump.target_type,
                    targetId: jump.target_id,
                    label: jump.label,
                  }),
                }));
                return;
              }
              // Everything else shows in the tool trail with a live duration…
              patchLast((m) => ({
                ...m,
                tools: [
                  ...(m.tools ?? []),
                  { id: e.id, label: e.label, startedAt: Date.now() },
                ],
              }));
              // The summary is a signal rather than work: `refresh_summary` writes
              // nothing, so there is no later moment to wait for.
              if (e.key === SUMMARY_REFRESH_KEY) {
                // The agent asks the page to regenerate the summary — hard refresh.
                refreshSummary().catch(() => undefined);
              }
            },
            onActivityEnd: (e: ChatActivityEvent) => {
              endTool(e);
              receiptFor(e);
              // A tree the agent wrote. Handled on the END and not the start,
              // because the start fires on the tool CALL, before the split exists:
              // refetching then returns the tree as it was, and a build could land
              // fifteen splits with the pane never moving. rgip replays the start's
              // payload here for exactly this (rgip #320), so the key and the tree
              // are both on this event. Emits coalesce, so a run of writes is one
              // refetch rather than fifteen.
              const treeRefresh = treeRefreshFromActivity(e);
              if (treeRefresh) {
                emitTreeStep({
                  at: Date.now(),
                  label: e.label || 'Changed the tree',
                  treeId: treeRefresh.treeId,
                });
              }
              // The same for signals. One document per model, so there is no
              // target to carry: the page reloads the signals it has.
              if (isSignalsRefreshActivity(e)) emitSignalsRefresh();
              // A rules write, and a base or threshold change: the Rules page, Fine
              // tuning and the model page reload what they show.
              if (isRulesRefreshActivity(e)) emitRulesRefresh();
              // A rename is a models-list write, and it changes this page's title too.
              if (isModelRefreshActivity(e) || isModelsRefreshActivity(e))
                emitModelRefresh();
            },
            onArtifactStart: (e) =>
              patchLast((m) => ({
                ...m,
                parts: openBlock(m.parts ?? [], {
                  key: e.key,
                  kind: e.kind,
                  title: e.title,
                  format: blockFormat(e.format),
                }),
              })),
            onArtifactDelta: (e) =>
              patchLast((m) => ({
                ...m,
                parts: appendBlockDelta(m.parts ?? [], e.key, e.delta),
              })),
            onArtifactEnd: (e) =>
              patchLast((m) => ({
                ...m,
                parts: closeBlock(m.parts ?? [], e.key),
              })),
            // Meta only, no content: bind the id so the block can be re-fetched.
            onArtifact: (e) => {
              patchLast((m) => ({
                ...m,
                parts: bindDeliverable(
                  m.parts ?? [],
                  e.artifact_key,
                  e.deliverable_id
                ),
              }));
              // A block whose numbers rgip merges in server-side is incomplete until
              // it is persisted, so what streamed here is the model's fragment and
              // not the card. Fetch the stored body and swap it in. Best-effort: a
              // failure leaves the streamed body, which renders as the placeholder
              // rather than as a card with missing numbers.
              if (SERVER_MERGED_BLOCKS.has(e.artifact_key)) {
                fetchDeliverableContent(tenantId, e.deliverable_id)
                  .then((raw) => {
                    if (!raw) return;
                    patchLast((m) => ({
                      ...m,
                      parts: replaceBlockBody(
                        m.parts ?? [],
                        e.artifact_key,
                        raw
                      ),
                    }));
                  })
                  .catch(() => undefined);
              }
            },
            onError: (msg) => {
              failed = true;
              patchLast((m) => ({
                ...m,
                parts: hasContent(m.parts ?? [])
                  ? m.parts ?? []
                  : appendText(m.parts ?? [], `⚠ ${msg}`),
              }));
            },
          },
          controller.signal
        );
      } catch (e) {
        failed = true;
        detached = !stoppedRef.current && isAbort(e);
        // Stopping aborts the reader, which surfaces here as a fetch abort. That is
        // the expected path out of a stopped turn, not a failure, so it must not be
        // reported as one.
        if (stoppedRef.current) {
          patchLast((m) => ({ ...m, stopped: true }));
        } else {
          patchLast((m) => ({
            ...m,
            parts: hasContent(m.parts ?? [])
              ? m.parts ?? []
              : appendText(
                  m.parts ?? [],
                  `⚠ ${e instanceof Error ? e.message : 'Ask failed.'}`
                ),
          }));
        }
      } finally {
        tracked.ended({ stopped: stoppedRef.current || detached, failed });
        setAsking(false);
        setStopping(false);
        runIdRef.current = null;
        // Freeze the turn timer at its total (the live clock stops ticking).
        patchLast((m) =>
          m.startedAt != null
            ? { ...m, elapsedMs: Date.now() - m.startedAt }
            : m
        );
        // A turn that made a copy ends by pointing at it, and the work continues there:
        // the conversation has already moved onto it server-side and every change has to
        // name the model on screen. So go, rather than leaving a question on the page it
        // cannot be answered from.
        //
        // This is what Studio's own duplicate button does (DuplicationModelModal), and
        // it is safe here for the same reason it is there: the turn is over. The agent
        // is told to stop at the copy, so nothing is streaming when the page moves, and
        // the reply is not lost either — the conversation replays on the copy, this turn
        // included.
        //
        // Only when the turn actually produced a jump to another model, which is only a
        // duplication, and only when it got all the way there. `finally` runs on the
        // failure path too, and navigating then would carry the reader away from the
        // error that had just been written into the answer.
        const target = modelJumpRef.current;
        modelJumpRef.current = null;
        if (target && !stoppedRef.current && !failed) {
          markHandoff(target);
          onFocusModel?.(target);
        }
      }
    },
    [
      input,
      asking,
      readOnly,
      agent,
      tenantId,
      modelId,
      summary,
      modelCopies,
      followupThread,
      refreshSummary,
      patchStopFailed,
      onFocusModel,
      memoryOn,
    ]
  );

  // Stop the turn. Two halves, and only the second one actually stops anything:
  // aborting the reader settles this panel immediately, while `cancelRun` is what
  // halts the worker. Without it the agent runs to the end of the turn unwatched,
  // which for a tree-building turn means it keeps writing splits.
  const stop = useCallback(async () => {
    if (!asking || stoppedRef.current) return;
    stoppedRef.current = true;
    setStopping(true);
    const runId = runIdRef.current;
    abortRef.current?.abort();
    if (!runId) return;
    try {
      await cancelRun(tenantId, runId);
    } catch {
      // The run may have finished in the same moment, which the server treats as a
      // no-op. Say the work might still be going rather than claim a clean stop.
      patchStopFailed();
    }
  }, [asking, tenantId, patchStopFailed]);

  // Asked from the Overview's prompts. Subscribed rather than passed down: this
  // panel is mounted once by ModelPage and those prompts render several levels
  // below it, with no prop path between them.
  useEffect(
    () =>
      onAsk((question) => {
        // OPEN first, both halves of it. Asking from the page is an explicit request
        // for an answer, and the turn has nowhere to render until the panel is really
        // there: `requested` is what makes the shell reserve the column, and `railed`
        // is the fold. Clearing only the fold left a question handed over from the
        // models list streaming into a column that was never reserved, which looks
        // exactly like the handoff having done nothing.
        if (!onPage) {
          setRequested(true);
          setRailed(false);
        }
        void ask(question).catch(() => undefined);
      }),
    [ask, onPage, setRailed, setRequested]
  );

  // Flag off (or still resolving) → render nothing: the model page, unchanged.
  if (enabled !== true) return null;

  // Fixed to the viewport, floating just inside <main>'s box — a rounded card
  // with breathing room, like admin's chat, rather than a panel glued to the
  // screen edge.
  const fixedStyle: React.CSSProperties = {
    top: box.top + 12,
    height: box.height - 24,
    right: 16,
  };

  // The overview's opening state is a hero, laid out like Ask Data Studio on
  // Models home: heading, composer, then the starters. The composer moves in the
  // markup, not with CSS order, so keyboard order follows what is on screen.
  // Only while the starters are what is showing: the sessions list keeps the
  // ordinary layout.
  const hero = onPage && messages.length === 0 && !showSessions;
  const composer = (
    <form
      className="mcp-ask"
      onSubmit={(e) => {
        e.preventDefault();
        ask().catch(() => undefined);
      }}
    >
      <textarea
        className="mcp-ask-input"
        placeholder="Ask a question about this model…"
        rows={1}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          // Enter sends, Shift+Enter makes a newline — same as admin's chat.
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            ask().catch(() => undefined);
          }
        }}
        disabled={asking || readOnly}
      />
      {asking ? (
        // Replaces Send rather than sitting beside it: while a turn runs there is
        // nothing to send, and Stop is the only thing the user can do.
        <button
          className="mcp-ask-stop"
          type="button"
          onClick={() => {
            stop().catch(() => undefined);
          }}
          disabled={stopping}
        >
          {stopping ? 'Stopping…' : 'Stop'}
        </button>
      ) : (
        <button
          className="mcp-ask-send"
          type="submit"
          disabled={!input.trim() || readOnly}
        >
          Send
        </button>
      )}
    </form>
  );

  const content = (
    <>
      {/* In-flow spacer reserves the 360px column so page content never slides
          under the fixed panel; the panel itself is fixed to the viewport.
          Docked, the grid column reserves it instead and this would double it. */}
      {!shellHosted && !onPage && <div className="mcp-spacer" aria-hidden />}
      {/* Docked, the column is the layout. Fixing this to the viewport would put
          it where its own spacer is not: once the content column is capped and
          centred, the two sit ~300px apart on a wide screen. */}
      <aside
        className={`mcp-rail${messages.length === 0 ? ' mcp-rail--empty' : ''}${
          onPage ? ' mcp-rail--page' : ''
        }${hero ? ' mcp-rail--hero' : ''}`}
        style={shellHosted || onPage ? undefined : fixedStyle}
        aria-label="Model chat"
      >
        <div className="mcp-head">
          <div className="mcp-head-text">
            <span className="mcp-title">Ask about this model</span>
          </div>
          {/* The way out of a resumed conversation. The panel now reopens whatever this
              person was last saying about this model, which is right nearly always and
              wrong the moment they want to start something unrelated; without this
              there would be no way back to an empty panel at all.

              Hidden while a turn runs: the turn would keep going on the old thread and
              answer into a conversation the reader has left, and Stop is right there. */}
          {/* Only when there is something to list. A control that opens an empty
              drawer is worse than no control. One group, so the hero card can place
              the controls together: positioned one by one they all landed on the
              same corner and drew over each other. */}
          <div className="mcp-head-actions">
            {sessions.length > 0 && !asking && !showMemory && (
              <button
                type="button"
                className="mcp-head-new"
                onClick={() => setShowSessions((open) => !open)}
                aria-expanded={showSessions}
                aria-label="Your earlier conversations about this model"
                title="Your earlier conversations about this model"
              >
                {showSessions ? 'Back' : 'My sessions'}
              </button>
            )}
            {memoryOn && !asking && (
              <button
                type="button"
                className="mcp-head-new"
                data-testid="chat-memory"
                onClick={() => {
                  setShowSessions(false);
                  setShowMemory((open) => !open);
                }}
                aria-expanded={showMemory}
                title="Decisions the assistant follows on this model"
              >
                {showMemory ? 'Back' : 'Memory'}
              </button>
            )}
            {messages.length > 0 && !asking && (
              <button
                type="button"
                className="mcp-head-new"
                onClick={startNewChat}
                aria-label="Start a new conversation"
                title="Start a new conversation"
              >
                {/* Worded, not an icon. An icon-only control here would be the second
                  glyph in a one-line header, and the first draft used a name no other
                  file in this repo uses, which renders as nothing at all and leaves a
                  button that is simply invisible rather than obviously broken. */}
                New chat
              </button>
            )}
          </div>
          {/* Only docked. Closing means folding the shell's column to its
              bubble, and there is no bubble to fold to in the floating layout,
              so the control would have nowhere to put the panel back from. */}
          {shellHosted && !onPage && (
            <button
              type="button"
              className="mcp-head-close"
              onClick={() => setRailed(true)}
              aria-label="Close the assistant"
              title="Close"
            >
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          )}
        </div>
        {hero && composer}

        {memoryOn && !showMemory && !showSessions && (
          <>
            {memoryNotice && (
              <p className="mem-detail mem-notice">{memoryNotice}</p>
            )}
            <MemoryBanner
              tenant={tenantId}
              list={memoryList}
              onChanged={onMemoryChanged}
            />
          </>
        )}

        <div
          className={`mcp-scroll${
            messages.length === 0 ? ' mcp-scroll--empty' : ''
          }`}
          ref={scrollRef}
          onScroll={onScroll}
        >
          {showMemory && (
            <>
              {memoryNotice && (
                <p className="mem-detail mem-notice">{memoryNotice}</p>
              )}
              <MemoryDrawer
                tenant={tenantId}
                modelId={modelId}
                list={memoryList}
                error={memoryError}
                onChanged={onMemoryChanged}
              />
            </>
          )}
          {/* Earlier conversations about this model, when asked for. Replaces the
              transcript rather than sitting above it, because it is a place you go
              and come back from, and the header control says Back while you are in it. */}
          {showSessions && (
            <div className="mcp-sessions">
              {sessions.map((session) => (
                <button
                  type="button"
                  key={session.thread_id}
                  className="mcp-session"
                  onClick={() => openSession(session.thread_id)}
                >
                  <span className="mcp-session-title">
                    {session.title?.trim() || 'Untitled conversation'}
                  </span>
                  {session.preview?.trim() && (
                    <span className="mcp-session-preview">
                      {session.preview}
                    </span>
                  )}
                  <span className="mcp-session-when">
                    {/* Which version of the model it was about: a model switched
                        between trees and rules keeps both conversations. */}
                    <span className="mcp-session-base">
                      {(session.agent ?? TREES_AGENT) === RULES_AGENT
                        ? 'Rules'
                        : 'Trees'}
                    </span>
                    {' · '}
                    {whenReadable(session.updated_at)}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* The empty state, as one composition rather than a stack of starters
              alone in the middle of the column. The mark is the launcher bubble's
              own glyph, so the panel reads as what the bubble became. */}
          {messages.length === 0 && !showSessions && !showMemory && (
            <div className="mcp-empty">
              <span className="mcp-empty-mark" aria-hidden="true">
                <Icon icon="far fa-sparkles" />
              </span>
              <p className="mcp-empty-title">What would you like to know?</p>
              <div className="mcp-suggest">
                {modelQuestions(isLive).map((suggestion) => (
                  <button
                    type="button"
                    className="mcp-suggest-btn"
                    key={suggestion}
                    onClick={() => {
                      ask(suggestion).catch(() => undefined);
                    }}
                    disabled={asking}
                  >
                    <span className="mcp-suggest-text">{suggestion}</span>
                    {/* Wrapped rather than styled directly, because the
                          FontAwesome kit replaces the <i> with an <svg> of its
                          own and the span is the element this file can rely on
                          still being there. */}
                    <span className="mcp-suggest-go" aria-hidden="true">
                      <i className="fas fa-arrow-right" />
                    </span>
                  </button>
                ))}
                {/* Offered here rather than reopened on arrival. Coming to a model is
                    usually a reason to ask about the model, and being dropped into the
                    middle of something from last week is not what was asked for. Last
                    in the list because it is the one option that is not a question. */}
                {sessions.length > 0 && (
                  <button
                    type="button"
                    className="mcp-suggest-btn"
                    onClick={() => setShowSessions(true)}
                  >
                    <span className="mcp-suggest-text">
                      Continue a previous session
                    </span>
                    <span className="mcp-suggest-go" aria-hidden="true">
                      <i className="fas fa-arrow-right" />
                    </span>
                  </button>
                )}
              </div>
            </div>
          )}
          {/* Hidden while the session list is open. The list is a place you go, not a
              banner over the conversation: leaving the transcript underneath it made
              the list read as an insert at the top of a chat that was still there. */}
          {!showSessions &&
            !showMemory &&
            messages.map((m, i) => {
              // A carried message is a record of the other assistant's conversation:
              // nothing on it is live, even when nothing has been asked here yet.
              const carried = i < carriedCount;
              const isLast = !carried && i === messages.length - 1;
              // Drawn after the last carried message, where the conversation changed hands.
              const handedOver = i === carriedCount - 1 && (
                <div className="mcp-handover" data-testid="chat-carried-over">
                  <span>
                    Continued with the{' '}
                    {(threadAgent ?? agent) === RULES_AGENT ? 'rules' : 'tree'}{' '}
                    assistant
                  </span>
                </div>
              );
              // A choices block is live only on the newest turn. Anything older is
              // answered, superseded, or about a tree that has moved since, so it renders
              // as a record with no reason attached.
              //
              // On the newest turn, everything that mutes it says why, in one expression
              // and in one place. They had drifted: three conditions disabled the buttons
              // and one of them supplied a caption, so two of the three drew a dead
              // control with no tooltip and nothing on screen to read. `canChoose` is
              // derived FROM the reason rather than computed beside it, which is what
              // stops a fourth condition arriving without one.
              //
              // A LIVE model is deliberately not among them: it is read-only rather than
              // unusable, the page is read-only on one too, and a change refused on a live
              // model comes back with the offer to copy it, which is the most useful
              // answer in the whole flow.
              const heldBack = isLast
                ? whyHeldBack({
                    asking,
                    isDuplicating,
                    isReady,
                    offersElsewhere: offersAnotherModel(m.parts, modelId),
                    otherBase: readOnly,
                  })
                : undefined;
              const canChoose = isLast && !heldBack;
              const inFlight = asking && isLast && m.role === 'assistant';
              // Has the turn produced anything renderable yet? Drives the pending
              // ellipsis and whether Thinking is held open.
              const answered = hasContent(m.parts ?? []);
              // Empty mid-stream on purpose: half an answer is worse to copy than none.
              const copyable = inFlight ? '' : answerText(m.parts);
              const status = inFlight
                ? liveStatus(m.tools ?? [], answered)
                : null;
              const muted = carried ? ' mcp-row--carried' : '';
              if (m.role === 'user') {
                return (
                  <React.Fragment key={i}>
                    <div className={`mcp-row mcp-row--user${muted}`}>
                      <div className="mcp-bubble mcp-bubble--user">
                        {m.content}
                      </div>
                    </div>
                    {handedOver}
                  </React.Fragment>
                );
              }
              return (
                <React.Fragment key={i}>
                  <div className={`mcp-row mcp-row--assistant${muted}`}>
                    {/* The model's own thinking: streamed live and held open while
                    a toggle the user opens. Never forced open: it is internal
                    chatter, and on a live run it confidently described tools
                    that appear nowhere in its prompt. */}
                    {m.reasoning && (
                      <details className="mcp-think">
                        <summary className="mcp-think-summary">
                          Thinking
                        </summary>
                        <div className="mcp-think-body">{m.reasoning}</div>
                      </details>
                    )}
                    {(m.tools?.length ?? 0) > 0 && (
                      <ToolTrail tools={m.tools as ToolRun[]} now={now} />
                    )}
                    {inFlight && status && <LiveStatus label={status} />}
                    {/* The turn, in the order the agent produced it. */}
                    {(m.parts ?? []).map((part, j) => (
                      <TurnPart
                        key={j}
                        part={part}
                        onFocus={focusFromPill}
                        onChoose={
                          canChoose
                            ? (message) => {
                                setPicked((prev) => ({
                                  ...prev,
                                  [i]: message,
                                }));
                                ask(message, { suggested: true }).catch(
                                  () => undefined
                                );
                              }
                            : undefined
                        }
                        answeredKey={picked[i]}
                        heldBack={heldBack}
                      />
                    ))}

                    {m.stopped && (
                      <span className="mcp-stopped">
                        {m.stopFailed
                          ? 'Stopped watching, but the agent may still be working'
                          : 'Stopped'}
                      </span>
                    )}
                    {/* The turn's footer. Rendered only when it holds something: an empty
                    flex row still takes its gap, and every turn would grow one. */}
                    {(copyable ||
                      m.elapsedMs != null ||
                      (inFlight && m.startedAt != null)) && (
                      <div className="mcp-foot">
                        <CopyAnswer text={copyable} />
                        {(m.elapsedMs != null ||
                          (inFlight && m.startedAt != null)) && (
                          <span className="mcp-elapsed">
                            ⏱{' '}
                            {fmtDur(m.elapsedMs ?? now - (m.startedAt ?? now))}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {handedOver}
                </React.Fragment>
              );
            })}
          <div ref={bottomRef} />
        </div>

        {readOnly && !showSessions && !showMemory && (
          <div className="mcp-otherbase" data-testid="chat-other-base">
            <span>
              {threadAgent === RULES_AGENT ? 'Rules chat' : 'Tree chat'} · read
              only
            </span>
            <button
              type="button"
              className="mcp-otherbase-go"
              data-testid="chat-continue-here"
              title="Starts a new chat that keeps what you discussed here"
              onClick={() => {
                continueHere().catch(() => undefined);
              }}
              disabled={handingOff}
            >
              {handingOff
                ? 'Starting…'
                : `New ${agent === RULES_AGENT ? 'rules' : 'tree'} chat`}
            </button>
          </div>
        )}
        {!hero && composer}
        <p className="mcp-disclaimer">AI can make mistakes. Always verify</p>
      </aside>
    </>
  );

  // The page slot first, whatever the shell is doing. Both can exist at once: the
  // Overview offers a slot while the shell still holds a column for the other tabs.
  if (onPage) return createPortal(content, heroEl);
  if (!shellHosted) return content;
  // No host means the shell has the column closed or folded to its rail, and it
  // draws the affordance to bring it back; a floating panel here would be a
  // second one. Portals up because this renders far below the shell: it needs
  // the model's ids, so it stays owned by the page.
  return shellPanel.hostEl ? createPortal(content, shellPanel.hostEl) : null;
}
